<!-- Event Code Entry (no token in URL) -->
<div *ngIf="showEventCodeForm && !event && !loading" class="page-wrapper">
  <div class="event-code-card">
    <h2>Find Your Event</h2>
    <p>Enter the event code from your invitation to get started.</p>
    <form (ngSubmit)="onSubmitEventCode()" class="event-code-form">
      <input
        type="text"
        [(ngModel)]="eventCode"
        name="eventCode"
        placeholder="Event code (e.g. S123)"
        class="event-code-input"
        [disabled]="searching"
        required
      />
      <button type="submit" class="btn btn-primary event-code-btn" [disabled]="searching || !eventCode.trim()">
        {{ searching ? 'Searching...' : 'Submit' }}
      </button>
    </form>
  </div>
  <div *ngIf="searchError" class="event-code-error">{{ searchError }}</div>
</div>

<!-- Error State -->
<div *ngIf="error" class="page-wrapper">
  <div class="error-card">
    <h2>Unable to Load Event</h2>
    <p>{{ error }}</p>
  </div>
</div>

<!-- Loading Skeleton -->
<div *ngIf="loading && !event" class="skeleton-page">
  <!-- Hero skeleton -->
  <div class="skel-hero"></div>
  <div class="page-wrapper">
    <div class="skel-bar" style="width: 60%; height: 28px; margin-bottom: 16px;"></div>
    <div class="skel-bar" style="width: 40%; height: 18px; margin-bottom: 24px;"></div>
    <div class="skel-bar" style="width: 100%; height: 48px; margin-bottom: 32px; border-radius: 6px;"></div>
    <div class="skel-bar" style="width: 30%; height: 22px; margin-bottom: 12px;"></div>
    <div class="skel-bar" style="width: 80%; height: 14px; margin-bottom: 8px;"></div>
    <div class="skel-bar" style="width: 70%; height: 14px; margin-bottom: 8px;"></div>
    <div class="skel-bar" style="width: 60%; height: 14px; margin-bottom: 8px;"></div>
    <div class="skel-bar" style="width: 50%; height: 14px; margin-bottom: 32px;"></div>
    <div class="skel-bar" style="width: 25%; height: 22px; margin-bottom: 12px;"></div>
    <div style="display: flex; gap: 12px; margin-bottom: 16px;">
      <div class="skel-avatar"></div>
      <div style="flex: 1;">
        <div class="skel-bar" style="width: 50%; height: 14px; margin-bottom: 6px;"></div>
        <div class="skel-bar" style="width: 40%; height: 12px; margin-bottom: 4px;"></div>
        <div class="skel-bar" style="width: 35%; height: 12px;"></div>
      </div>
    </div>
  </div>
</div>

<!-- Event Detail Page -->
<div *ngIf="event && !loading">

  <!-- Hero Image -->
  <!-- TODO: hero_image — needs backend: add hero_image field to events table or use topic.image -->
  <div class="hero-image-placeholder">
    <div class="hero-overlay"></div>
  </div>

  <div class="page-wrapper">

    <!-- Event Title Row -->
    <div class="event-title-row">
      <h1 class="event-title">{{ event.topic || event.name }}</h1>
      <div *ngIf="event.topicProducts?.length" class="event-product-badges">
        <img
          *ngFor="let product of event.topicProducts"
          [src]="product.image"
          [alt]="product.name"
          class="event-company-badge"
        />
      </div>
    </div>

    <div class="event-content-grid">

      <!-- Left Column: RSVP + Event Details -->
      <div class="event-col-left">

        <!-- Canceled Event State -->
        <div *ngIf="isCanceled" class="canceled-card">
          <p class="canceled-text">This event has been canceled</p>
        </div>

        <!-- Declined State (from attendee status or user action) -->
        <div *ngIf="!isCanceled && isDeclinedStatus" class="decline-card">
          <h3>You've declined attending this event</h3>
          <p>Changed your mind? <a href="#" class="contact-link" (click)="$event.preventDefault()">Contact us here</a></p>
        </div>

        <!-- RSVP Buttons (only if not canceled, not declined, not already attending) -->
        <div *ngIf="showRsvpCard" class="rsvp-card">
          <ng-container *ngIf="isConfirmed; else notAcceptingRsvp">
            <p class="rsvp-text">
              <ng-container *ngIf="attendeeLabel">{{ attendeeLabel }}, you've been invited to attend!</ng-container>
              <ng-container *ngIf="!attendeeLabel">You've been invited to attend!</ng-container>
            </p>
            <div class="rsvp-actions">
              <button class="btn btn-outlined rsvp-btn" (click)="decline()" [disabled]="declining">Decline</button>
              <button class="btn btn-primary rsvp-btn" (click)="openRegistration()">Accept</button>
            </div>
            <p *ngIf="attendeeLabel" class="rsvp-not-you">Not {{ attendeeLabel }}? <a href="#" (click)="$event.preventDefault(); ignoreAttendee()">Click Here</a></p>
          </ng-container>
          <ng-template #notAcceptingRsvp>
            <p class="rsvp-text">Thank you for your interest. We are not able to accept new registrations at this time.</p>
          </ng-template>
        </div>

        <!-- Event Details Card -->
        <section class="detail-section">
          <div class="section-title-row">
            <h2 class="section-title">Event Details</h2>
            <span *ngIf="statusPill" class="pill-badge" [ngClass]="statusPill.cssClass">{{ statusPill.text }}</span>
          </div>

          <!-- Date -->
          <div class="detail-row" *ngIf="event.friendlyDate">
            <div class="detail-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" fill="currentColor"/></svg>
            </div>
            <div class="detail-content">
              <span class="detail-label">Event Date</span>
              <span class="detail-value">{{ event.friendlyDate }} {{ event.startTime }} {{ formatTimezone() }}</span>
            </div>
          </div>

          <!-- Presentation -->
          <div class="detail-row" *ngIf="event.topic">
            <div class="detail-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7l-2 3v1h8v-1l-2-3h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 12H3V4h18v10z" fill="currentColor"/></svg>
            </div>
            <div class="detail-content">
              <span class="detail-label">Presentation</span>
              <span class="detail-value">{{ event.topic }}</span>
            </div>
          </div>

          <!-- Meeting Type -->
          <div class="detail-row" *ngIf="event.meetingType">
            <div class="detail-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/></svg>
            </div>
            <div class="detail-content">
              <span class="detail-label">Meeting Type</span>
              <span class="detail-value">{{ event.meetingType }}</span>
            </div>
          </div>

          <!-- Location + Map -->
          <div class="location-block" *ngIf="event.venue">
            <div class="detail-row detail-row--no-border">
              <div class="detail-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" fill="currentColor"/></svg>
              </div>
              <div class="detail-content">
                <span class="detail-label">Location</span>
                <span class="detail-value">{{ event.venue.name }}</span>
                <span class="detail-subvalue" *ngIf="formatVenueAddress()">{{ formatVenueAddress() }}</span>
              </div>
              <a
                *ngIf="getGoogleMapsUrl() as mapsUrl"
                [href]="mapsUrl"
                target="_blank"
                rel="noopener"
                class="detail-action"
                aria-label="Open in Google Maps"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="currentColor"/></svg>
              </a>
            </div>

            <a
              *ngIf="getStaticMapUrl() as mapUrl"
              [hidden]="mapImageFailed"
              [href]="getGoogleMapsUrl()"
              target="_blank"
              rel="noopener"
              class="venue-map-container"
              aria-label="Open in Google Maps"
            >
              <img [src]="mapUrl" alt="Map showing venue location" class="venue-map-image"
                [style.transform]="getMapImageTransform()" loading="lazy"
                (error)="mapImageFailed = true" />
              <svg class="venue-map-pin" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg"
                [style.left]="getMapPinPosition()?.left"
                style="top: 50%">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24S24 21 24 12C24 5.373 18.627 0 12 0z" fill="#e53935"/>
                <circle cx="12" cy="12" r="5" fill="white"/>
              </svg>
            </a>
          </div>

          <!-- Event ID -->
          <div class="detail-row" *ngIf="event.eventDisplayId">
            <div class="detail-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zm-5-4H7v-2h8v2zm2-4H7V8h10v2z" fill="currentColor"/></svg>
            </div>
            <div class="detail-content">
              <span class="detail-label">Event ID</span>
              <span class="detail-value">{{ event.eventDisplayId }}</span>
            </div>
          </div>
        </section>

      </div><!-- /event-col-left -->

      <!-- Right Column: People + About -->
      <div class="event-col-right">

        <!-- People Section (first on mobile; About floats above on desktop via order) -->
        <section class="detail-section" *ngIf="event.speakers?.length || event.hosts?.length">
          <h2 class="section-title">People</h2>

          <!-- Speakers -->
          <ng-container *ngFor="let speaker of event.speakers; let last = last">
            <div class="person-card">
              <div class="person-photo-wrapper">
                <img
                  *ngIf="speaker.photo"
                  [src]="speaker.photo"
                  [alt]="speaker.fullName"
                  class="person-photo"
                />
                <div *ngIf="!speaker.photo" class="person-photo-placeholder">
                  {{ speaker.firstName.charAt(0) }}{{ speaker.lastName.charAt(0) }}
                </div>
              </div>
              <div class="person-info">
                <span class="person-name">
                  {{ speaker.fullName }}{{ speaker.degrees ? ', ' + speaker.degrees : '' }}
                </span>
                <span class="person-title" *ngIf="speaker.title">{{ speaker.title }}</span>
                <span class="person-affiliation" *ngIf="speaker.affiliation">{{ speaker.affiliation }}</span>
                <span class="person-role">{{ speaker.role }}</span>
              </div>
            </div>
            <hr *ngIf="!last" class="person-divider" />
          </ng-container>

          <!-- Divider between speaker and host groups -->
          <hr *ngIf="event.speakers?.length && event.hosts?.length" class="person-divider" />

          <!-- Hosts -->
          <ng-container *ngFor="let host of event.hosts; let last = last">
            <div class="person-card">
              <div class="person-photo-wrapper">
                <img
                  *ngIf="host.photo"
                  [src]="host.photo"
                  [alt]="host.fullName"
                  class="person-photo"
                />
                <div *ngIf="!host.photo" class="person-photo-placeholder">
                  {{ host.firstName.charAt(0) }}{{ host.lastName.charAt(0) }}
                </div>
              </div>
              <div class="person-info">
                <span class="person-name">{{ host.fullName }}</span>
                <span class="person-title" *ngIf="host.title">{{ host.title }}</span>
                <span class="person-affiliation" *ngIf="host.company">{{ host.company }}</span>
                <span class="person-role">{{ host.role }}</span>
              </div>
            </div>
            <hr *ngIf="!last" class="person-divider" />
          </ng-container>
        </section>

        <!-- About the Event -->
        <section class="detail-section about-section" *ngIf="event.topicDescription">
          <h2 class="section-title">About the Event</h2>
          <div class="about-content" [innerHTML]="event.topicDescription"></div>
        </section>

        <!-- TODO: about_text — populate topic.description in the admin dashboard or add event-level description field -->
        <section class="detail-section about-section" *ngIf="!event.topicDescription">
          <h2 class="section-title">About the Event</h2>
          <p class="about-placeholder">Event description will be available soon.</p>
        </section>

      </div><!-- /event-col-right -->

    </div><!-- /event-content-grid -->

  </div>
</div>

<!-- Registration Modal -->
<app-registration-modal
  *ngIf="showModal && eventForModal"
  [event]="eventForModal!"
  (submitted)="onRegistrationSuccess()"
  (cancelled)="closeRegistration()"
></app-registration-modal>
